/**
 * Client helper for the per-demand progress stream. Wraps `EventSource` with
 * automatic reconnection (the browser reconnects on transient drops, but not
 * after some errors, so we re-open with a small backoff). Feature screens call
 * this after an upload or analysis to render live tool activity and status.
 *
 * Runs in the browser bundle; not unit-tested here — the stream is exercised
 * end to end by tests/e2e/jobs.spec.ts.
 */
export interface DemandEventStream {
  close: () => void;
}

export function connectDemandEvents(
  orgId: string,
  demandId: string,
  onEvent: (type: string, data: unknown) => void,
  opts: { reconnectMs?: number } = {},
): DemandEventStream {
  const url = `/api/orgs/${encodeURIComponent(orgId)}/demands/${encodeURIComponent(demandId)}/events`;
  const reconnectMs = opts.reconnectMs ?? 2000;
  let source: EventSource | undefined;
  let closed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const handle = (event: MessageEvent): void => {
    const raw = String(event.data);
    let data: unknown;
    try {
      data = raw === '' ? {} : (JSON.parse(raw) as unknown);
    } catch {
      data = { raw };
    }
    onEvent(event.type, data);
  };

  const open = (): void => {
    if (closed) return;
    source = new EventSource(url, { withCredentials: true });
    // Named events (extract.progress, extract.done, job.*, ready, ping) arrive as
    // typed events; listen broadly by re-dispatching through one handler.
    for (const type of [
      'ready',
      'ping',
      'extract.progress',
      'extract.done',
      'job.done',
      'job.failed',
      'analysis.progress',
      'analysis.done',
    ]) {
      source.addEventListener(type, handle as EventListener);
    }
    source.onmessage = handle;
    source.onerror = () => {
      source?.close();
      if (!closed) timer = setTimeout(open, reconnectMs);
    };
  };

  open();

  return {
    close: () => {
      closed = true;
      if (timer !== undefined) clearTimeout(timer);
      source?.close();
    },
  };
}

/**
 * Client helper for a conversation's chat stream (HS2-ZXJJSS / HS2-Q19SYC): the
 * same reconnecting EventSource wrapper, but for the conversation-keyed channel
 * so demand and library/project chat both stream live tool-activity and text.
 */
export function connectConversationEvents(
  orgId: string,
  conversationId: string,
  onEvent: (type: string, data: unknown) => void,
  opts: { reconnectMs?: number } = {},
): DemandEventStream {
  const url = `/api/orgs/${encodeURIComponent(orgId)}/conversations/${encodeURIComponent(conversationId)}/events`;
  const reconnectMs = opts.reconnectMs ?? 2000;
  let source: EventSource | undefined;
  let closed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const handle = (event: MessageEvent): void => {
    const raw = String(event.data);
    let data: unknown;
    try {
      data = raw === '' ? {} : (JSON.parse(raw) as unknown);
    } catch {
      data = { raw };
    }
    onEvent(event.type, data);
  };

  const open = (): void => {
    if (closed) return;
    source = new EventSource(url, { withCredentials: true });
    for (const type of ['ready', 'ping', 'chat.start', 'chat.tool', 'chat.delta', 'chat.done']) {
      source.addEventListener(type, handle as EventListener);
    }
    source.onmessage = handle;
    source.onerror = () => {
      source?.close();
      if (!closed) timer = setTimeout(open, reconnectMs);
    };
  };

  open();

  return {
    close: () => {
      closed = true;
      if (timer !== undefined) clearTimeout(timer);
      source?.close();
    },
  };
}
